import { useState } from "react";
import {
  backupFileName,
  backupPhotoPath,
  buildBackupManifest,
  buildStockCsvs,
  stockAttachmentPath,
  type AttachmentMeta,
  type SessionUser,
} from "@iltocat/shared";
import { ApiError, api, fetchAttachment, fetchPhoto, plural } from "../lib/api.js";
import { allItems } from "../lib/allItems.js";
import { downloadBlob } from "../lib/download.js";
import { pooled } from "../lib/pool.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import type { Vocabulary } from "../lib/useVocabulary.js";
import { zipStore, type ZipFile } from "../lib/zip.js";
import { Icon } from "../components/Icon.js";
import { ItemCsvExport } from "../components/ItemCsvExport.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";

/**
 * Stage 5: "nobody feels locked in" -- every item the switcher currently
 * shows, as one zip: `manifest.json` (built by `buildBackupManifest()` in
 * `@iltocat/shared`) plus every photo at full size. Entirely client-side,
 * the same shape as the Stage 4 listing screen's photo zip, just for a whole
 * inventory instead of one item.
 *
 * Not one of the two exits `publicFields()` guards (see
 * docs/notes/invariants.md) -- this is a member's own copy of exactly what
 * they can already see on every item screen, private fields and notes
 * included, staying on their own device.
 *
 * `GET /api/items` answers with summaries; the whole item -- which is what
 * carries private fields and notes -- takes a second round trip per id. At
 * the ~100-item scale this app is sized for that is a few hundred requests
 * for items and photos combined, fetched a handful at a time (`pooled()`)
 * rather than all at once.
 */

const CONCURRENCY = 6;

type Progress = (stage: { label: string; done: number; total: number }) => void;

/**
 * A stockroom's half of the export: `buildStockCsvs()` over `GET
 * /api/stock/records`, plus every invoice and certificate the records point
 * at, fetched a few at a time like the photos.
 */
async function stockRecordFiles(onProgress: Progress): Promise<ZipFile[]> {
  onProgress({ label: "Reading the stock records", done: 0, total: 0 });
  const records = await api.stock.records();
  const encoder = new TextEncoder();
  const sheets = buildStockCsvs(records).map(({ name, text }) => ({ name, data: encoder.encode(text) }));

  const attachments = [
    ...records.receipts.map((receipt) => receipt.attachment),
    ...records.retirements.map((retirement) => retirement.certificate),
  ].filter((attachment): attachment is AttachmentMeta => attachment !== null);
  let fetched = 0;
  onProgress({ label: "Downloading invoices and certificates", done: 0, total: attachments.length });
  const files = await pooled(attachments, CONCURRENCY, async (attachment) => {
    const data = new Uint8Array(await (await fetchAttachment(attachment.id)).arrayBuffer());
    fetched += 1;
    onProgress({ label: "Downloading invoices and certificates", done: fetched, total: attachments.length });
    return { name: stockAttachmentPath(attachment), data };
  });
  return [...sheets, ...files];
}

export function Export({
  user,
  taxonomy,
  vocabulary,
}: {
  user: SessionUser;
  taxonomy: Taxonomy;
  vocabulary: Vocabulary;
}) {
  const [busy, setBusy] = useState(false);
  /** What is happening, and how far along, for the progress bar. */
  const [stage, setStage] = useState<{ label: string; done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const stock = user.activeKind === "stock";

  async function run() {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      setStage({ label: "Reading items", done: 0, total: 0 });
      // Retired stock too: the lists hide it, a backup is everything.
      const items = await allItems(
        (loaded, total) => setStage({ label: "Reading items", done: loaded, total }),
        {
          withRetired: true,
        },
      );

      const manifest = buildBackupManifest({
        inventoryId: user.activeInventoryId,
        locations: taxonomy.locations,
        categories: taxonomy.categories,
        tags: vocabulary.tags,
        items,
      });

      const photos = items.flatMap((item) => item.photos.map((photo) => ({ itemId: item.id, photo })));
      let fetched = 0;
      setStage({ label: "Downloading photos", done: 0, total: photos.length });
      const photoFiles = await pooled(photos, CONCURRENCY, async ({ itemId, photo }) => {
        const data = await fetchPhoto(photo.id);
        fetched += 1;
        setStage({ label: "Downloading photos", done: fetched, total: photos.length });
        return { name: backupPhotoPath(itemId, photo.id), data };
      });

      const stockFiles = stock ? await stockRecordFiles(setStage) : [];

      setStage({ label: "Packing the zip", done: 0, total: 0 });
      const files: ZipFile[] = [
        { name: "manifest.json", data: new TextEncoder().encode(JSON.stringify(manifest, null, 2)) },
        ...photoFiles,
        ...stockFiles,
      ];
      downloadBlob(
        new Blob([zipStore(files)], { type: "application/zip" }),
        backupFileName(user.activeInventoryId),
      );
      setDone(
        `Exported ${plural(items.length, "item")} and ${plural(photoFiles.length, "photo")}${
          stock ? ", with the stock records" : ""
        }.`,
      );
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The export did not finish.");
    } finally {
      setBusy(false);
      setStage(null);
    }
  }

  const percent = stage && stage.total > 0 ? Math.round((stage.done / stage.total) * 100) : null;

  return (
    <div className="stack">
      <PageHeader title="Export" />
      <Panel>
        <div className="feature-card">
          <span className="feature-icon">
            <Icon name="download" size={24} />
          </span>
          <div className="feature-text">
            <h2 className="panel-title">Everything, as a zip</h2>
            <p className="muted">
              Every item in this inventory as JSON, private notes and fields included, plus every photo at
              full size. Yours to keep, whatever happens to utucat.
            </p>
            {stock && (
              <p className="muted">
                A stockroom's records come too, as spreadsheets in a <code>stock</code> folder: stock,
                receipts, clients, deployments, retirements and every movement, with cost and invoice numbers,
                plus the invoices and certificates kept against them.
              </p>
            )}
          </div>
        </div>

        {error && <Notice tone="error">{error}</Notice>}
        {done && <Notice tone="ok">{done}</Notice>}

        {stage && (
          <div className="progress" aria-live="polite">
            <div className="progress-row">
              <span>{stage.label}…</span>
              {stage.total > 0 && (
                <span className="muted">
                  {stage.done} of {stage.total}
                </span>
              )}
            </div>
            <div
              className={percent === null ? "meter meter-busy" : "meter"}
              role="progressbar"
              aria-label={stage.label}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent ?? undefined}
            >
              <span style={{ width: `${percent ?? 100}%` }} />
            </div>
          </div>
        )}

        <div className="panel-foot">
          <button type="button" className="button" disabled={busy} onClick={() => void run()}>
            <Icon name="download" size={18} />
            {busy ? "Exporting…" : "Download everything"}
          </button>
        </div>
      </Panel>
      <ItemCsvExport inventoryId={user.activeInventoryId} taxonomy={taxonomy} stock={stock} />
    </div>
  );
}
